import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getServiceConfig, ServiceConfig, serviceConfigurations } from '../utils/serviceKeys';
import { fetchServiceConfigByKey } from '../utils/dbServiceKeys';

export function useServiceKey() {
  const params = useParams<{ serviceKey?: string; "*"?: string }>();
  const location = useLocation();
  const [serviceKey, setServiceKey] = useState<string | null>(null);
  const [serviceConfig, setServiceConfig] = useState<ServiceConfig>(getServiceConfig(null));
  const [isValidKey, setIsValidKey] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const currentPath = location.pathname;
    const splatParam = params["*"];
    const pathSegments = currentPath.split('/').filter(Boolean);

    // EXTENSIVE TRACING
    console.log('[TRACE] useServiceKey EFFECT RUN');
    console.log('[TRACE] Path:', currentPath);
    console.log('[TRACE] Params:', params);

    // Robust check for any /auth or /auth/callback or /auth/*
    const isAuthRoute =
      currentPath === '/auth' ||
      currentPath === '/auth/callback' ||
      currentPath.startsWith('/auth/') ||
      currentPath.startsWith('/auth') ||
      pathSegments.includes('auth') ||
      (splatParam && (splatParam === 'auth' || splatParam.startsWith('auth') || splatParam.includes('auth')));

    if (isAuthRoute) {
      setServiceKey('');
      setServiceConfig(getServiceConfig(''));
      setIsValidKey(false);
      setIsLoading(false);
      return;
    }

    // Extraction logic
    const extractKeyFromUrl = () => {
      if (currentPath.startsWith('/auth') || currentPath.includes('/auth/')) {
        return '';
      }
      if (params.serviceKey) {
        if (params.serviceKey === 'auth' || params.serviceKey === 'callback') {
          return '';
        }
        return params.serviceKey;
      }
      if (splatParam) {
        if (splatParam === 'auth' || splatParam.startsWith('auth') || splatParam.includes('auth') || splatParam === 'callback') {
          return '';
        }
        const segments = splatParam.split('/').filter(Boolean);
        if (segments.length > 0) {
          if (segments[0] === 'auth' || segments[0] === 'callback') {
            return '';
          }
          return segments[0];
        }
      }
      if (pathSegments.length > 0) {
        if (pathSegments[0] === 'auth' || pathSegments[0] === 'callback') {
          return '';
        }
        return pathSegments[0];
      }
      const urlParams = new URLSearchParams(location.search);
      const keyFromSearch = urlParams.get('key');
      if (keyFromSearch && keyFromSearch !== 'auth' && keyFromSearch !== 'callback') {
        return keyFromSearch;
      }
      return '';
    };

    setIsLoading(true);
    const extractedKey = extractKeyFromUrl();
    console.log('[TRACE] Extracted service key:', extractedKey);
    setServiceKey(extractedKey);

    // Check hardcoded configs first
    if (extractedKey && serviceConfigurations[extractedKey]) {
      const config = getServiceConfig(extractedKey);
      console.log('[TRACE] Valid service key (hardcoded):', extractedKey);
      setIsValidKey(true);
      setServiceConfig(config);
      applyTheming(config);
      setIsLoading(false);
      return;
    }

    // If not in hardcoded configs, try fetching from API
    if (extractedKey) {
      console.log('[TRACE] Key not in hardcoded configs, fetching from API:', extractedKey);
      fetchServiceConfigByKey(extractedKey).then((config) => {
        if (config) {
          console.log('[TRACE] Valid service key (from API):', extractedKey);
          setIsValidKey(true);
          setServiceConfig(config);
          applyTheming(config);
        } else {
          console.warn('[TRACE] Invalid service key:', extractedKey);
          setIsValidKey(false);
          setServiceConfig(getServiceConfig(null));
        }
        setIsLoading(false);
      });
      return;
    }

    // No key provided
    setIsValidKey(false);
    setServiceConfig(getServiceConfig(null));
    setIsLoading(false);
  }, [location.pathname, location.search, params]);

  return {
    serviceKey,
    serviceConfig,
    isValidKey,
    isLoading,
    attributes: serviceConfig.attributes || {}
  };
}

function applyTheming(config: ServiceConfig) {
  document.title = config.serviceName;
  document.documentElement.style.setProperty('--primary-color', config.primaryColor);
  if (config.attributes) {
    Object.entries(config.attributes).forEach(([attrKey, value]) => {
      document.documentElement.style.setProperty(`--${attrKey}`, String(value));
    });
  }
}
